/**
 * `tab-selector` — 17 screens (design-spec.md §5.5).
 * 52 high, radius 16, 4px padding and gap, on `white/5%` with a `white/8%`
 * border. The active tab is a radius-12 `#2C5DFF` fill at 15/SemiBold; the
 * others are unfilled at 15/Regular/white-96.
 */
export interface TabItem<T extends string> {
  value: T
  label: string
}

interface TabSelectorProps<T extends string> {
  items: readonly TabItem<T>[]
  value: T
  onChange: (value: T) => void
  /**
   * The trade form fills the active tab red on the sell side (`202:2279`);
   * the coin buy form (`202:2084`) fills it green.
   */
  tone?: 'primary' | 'danger' | 'success'
  'aria-label'?: string
}

const TONE_FILL = {
  primary: 'bg-primary',
  danger: 'bg-danger',
  success: 'bg-success-deep',
} as const

export function TabSelector<T extends string>({
  items,
  value,
  onChange,
  tone = 'primary',
  'aria-label': ariaLabel,
}: TabSelectorProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex h-tabbar w-full items-center gap-1 rounded-16 border border-line-8 bg-surface-5 p-1"
    >
      {items.map((item) => {
        const active = item.value === value
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={[
              'sx-tight h-[44px] flex-1 rounded-12 text-15 transition-colors duration-(--sx-dur)',
              active
                ? `${TONE_FILL[tone]} font-semibold text-ink`
                : 'font-normal text-ink-96',
            ].join(' ')}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
